import { pathToRegexp } from 'path-to-regexp';
import * as util from './routing.util';
import { AEM_PAGE_DELIMITER, BLACKLISTED_SEARCH_PARAMETERS } from './router.config';
import { initializeLogger } from '@seamless/logger';
import { isBrowser } from './routing.util';

const LOGGER = initializeLogger('SeamlessRouter');

/**
 * Holds relevant and parsed URL data for one or zero namespaces.
 * * `url` - the full, untreated, raw UR; always present
 * * `subPathname` - the current pathname for the page or namespace's pathname; always present
 * * `searchParams` - a plain JS Object with [key, value] pairs for all search parameters of the
 * context's namespace; only present if the context is *one* namespace
 */
export type URLData = {
  url: URL;
  subPathname: string;
  searchParams?: Record<string, string>;
};

/**
 * The composed object sent to registered URLChangeListeners for one specified namespace.
 * * `from` - the URL, and its data parts, for the state the page was in *before* the navigation
 * * `to` - the URL, and its data parts, for the state the page is in *after* navigation
 *
 * See also the type `URLData`.
 */
export type URLChangeEvent = {
  from: URLData;
  to: URLData;
};

/**
 * Options object for the `addURLChangeListener` function.
 * * `namespace` - modifies the listener behaviour so that:
 *   1. it's only fired when the data for the given `namespace` is modified
 *   2. it filters and pre-parsed data specific to the given `namespace`
 */
export type URLChangeOptions = {
  namespace?: string;
};

/**
 * The signature for URLChangeListeners functions.
 * `(event: URLChangeEvent) => void`
 */
export type URLChangeListenerFunction = (event: URLChangeEvent) => void;

/**
 * Internal type for storing URLChangeListeners. It stored both the listener and the options object side-by-side, so that
 * the internal URL change handler can modify its own behaviours for different kinds of listeners.
 *
 *  @ignore
 */
type URLChangeListener = URLChangeOptions & {
  handler: URLChangeListenerFunction;
};

/**
 * Internal representation of a namespaced URLChangeEvent entry. It is used to avoid repeated constructions
 * of the same data structure for different URLChangeListeners. The `event` property value is the one that is
 * actually sent outside the boundaried of this API.
 *
 * @ignore
 */
type URLChangeEventCacheEntry = {
  hasChanged: boolean;
  event: URLChangeEvent;
};

/**
 * Internal type for the cache objects generated with every triggered URL change. The keys are
 * listened namespaces, and the `null` entry is used for the global URL change listeners entry.
 *
 * @ignore
 */
type URLChangeEventCache = Map<string | null, URLChangeEventCacheEntry>;

/**
 * Options object for URL resolution.
 * * `namespace` - the target namespace for the remaining properties
 * * `path` - the pathname of the target URL
 * * `hash` - the hash of the target URL
 * *  `searchParams` - a plain object of key-value pairs of the target URL's search parameters
 */
export type URLInitOptions = {
  namespace: string;
  path?: string | null;
  pathname?: string | null;
  hash?: string | null;
  searchParams?: Record<string, string | null> | null;
  prefetch?: boolean | null;
};

export type URLModifierOptions = {
  url: URL | string;
  searchParams: Record<string, string>;
};

/**
 * Options object for URL clearance.
 * * `clear` - boolean to trigger link clear
 * * `base` - new url base path to be replaced
 */
export type URLGlobalOptions = {
  clear?: boolean;
  base?: string | null;
};

/**
 * Options object for URL matching.
 * * `exact` - boolean to trigger exact link matching
 */
export type URLMatchOptions = {
  exact: boolean;
};

/**
 * Hold the result of match attempt to the current page's URL.
 * * `isMatch` - true if the match succeeded, `false` otherwise
 * * `variables` - a plain object with the variables extracted from the URL, it is `undefined`
 * if it contains none, or if there was no match
 * * `searchParams` a plain object with the key-value pairs of the search parameters related
 * with the matched namespace
 */
export type URLMatchResult = {
  isMatch: boolean;
  variables?: Record<string, string>;
  searchParams?: Record<string, string>;
};

/**
 * Hold the result of match attempt to the current page's URL from an array of URLInitOptions.
 * * `isMatch` - true if the match succeeded, `false` otherwise
 * * `namespaces` - contains each namespace provided in the array
 * * * `variables` - a plain object with the variables extracted from the URL, it is `undefined`
 * if it contains none, or if there was no match
 * * * `searchParams` a plain object with the key-value pairs of the search parameters related
 * with the matched namespace
 */
export type URLMultipleMatchResult = {
  isMatch: boolean;
  namespaces?: Record<
    string,
    {
      variables?: Record<string, string>;
      searchParams?: Record<string, string>;
    }
  >;
};

/**
 * Holds the return object of getResolveRebasedTarget.
 * * `target` - the rebased target that is resolved based on the clear options
 * * `isSamePage` - boolean that indicates if the new target is a link to the same or to a different page
 */
type ResolutionTarget = {
  target: URL;
  isSamePage: boolean;
};

/**
 * The internal state of the routing library.
 *
 * @ignore
 */
type InternalState = {
  // This is the namespace of the web component which is allowed to control
  // the actual URL's pathname. Every other namespace's path changes will be
  // relegated to a searchParam. If it is `null`,there's no main namespace
  // and every namespace's pathname is controled via searchParam
  mainNamespace: string | null;
  // This is the URL's pathname for the current AEM Page
  // Example: https://www.mercedes-benz.de/foo/bar/index.html/bar/baz
  // AEM Page Pathname: /foo/bar/index.html
  basePathname: string;
  // List of all registered callbacks for URL changes
  urlChangeListeners: URLChangeListener[];
  // The reflection of the current page's URL as a native URL object
  // This is kept in sync with any URL modification done via the browser's History API
  // or external hash changes
  currentUrl: URL;
  // Flag indicating if `initRouting` has been called. Used on exported functions
  // to validate if they're being used only after initialization, as use before may
  // lead to undefined behavior
  isInitialized: boolean;
};

/**
 * The internal state of the routing library.
 */
let routerObject: InternalState;

/**
 * SSR URL override. Set via `setSSRUrl` before initialization.
 * @private
 */
let ssrUrlOverride: string | null = null;

/**
 * Sets the current URL for SSR context.
 * Call this before any router initialization in your SSR framework (e.g., Nuxt plugin).
 *
 * @example
 * ```typescript
 * // In Nuxt: plugins/seamless-router.server.ts
 * import { setSSRUrl } from '@seamless/router'
 *
 * export default defineNuxtPlugin(() => {
 *   const url = useRequestURL()
 *   setSSRUrl(url.href)
 * })
 * ```
 *
 * @export
 * @param {string} url - The full URL string of the current request
 */
export function setSSRUrl(url: string): void {
  if (URL.canParse(url)) {
    ssrUrlOverride = url;
    LOGGER.log('[sw-routing] SSR URL override set to:', url);
  } else {
    LOGGER.warn('[sw-routing] Invalid URL provided to setSSRUrl. It should be a full URL string. Ignoring override.');
  }
}

/**
 * Creates a URL for SSR context.
 * Handles invalid BASE_URL values gracefully (e.g., relative paths like '/').
 *
 * @private
 * @ignore
 * @returns {URL} A valid URL for SSR context
 */
function createSSRUrl(): URL {
  // Check for SSR URL override first (set by framework integration).
  // ssrUrlOverride is always a valid URL since setSSRUrl validates it before storing.
  if (ssrUrlOverride) {
    return new URL(ssrUrlOverride);
  }

  const baseUrl = process.env.BASE_URL;

  // If BASE_URL is not set or is a relative path, use localhost
  if (!baseUrl?.startsWith('http')) {
    return new URL('/', 'http://localhost');
  }

  try {
    return new URL('/', baseUrl);
  } catch {
    // Fallback if BASE_URL is somehow invalid
    return new URL('/', 'http://localhost');
  }
}

/**
 * The listener for the browser's History API 'popstate' event.
 *
 * @private
 * @ignore
 */
function globalPopStateListener(): void {
  triggerUrlChange();
}

/**
 * Internal function that reacts to any change to the page's URL and is responsible for:
 * 1. updating the routing library's internal state
 * 2. trigger any applicable URLChangeListeners
 *
 * @private
 * @ignore
 */
function triggerUrlChange(forceTrigger = false): void {
  if (!isBrowser()) {
    return;
  }

  const router = getRouterObject();
  const previousUrl = router.currentUrl;
  const currentUrl = new URL(String(window.location));
  router.currentUrl = currentUrl;

  const urlDataCache: Map<string | null, URLChangeEventCacheEntry> = new Map();
  router.urlChangeListeners.forEach((listener) => {
    const urlChangeEvent = composeUrlChangeEvent(listener, previousUrl, currentUrl, urlDataCache);
    if (urlChangeEvent.hasChanged || forceTrigger) {
      listener.handler(urlChangeEvent.event);
    }
  });
}

/**
 * Captures and composes the URL state at two points in time.
 *
 * Depending on the listener's configuration this can have one of two behaviours:
 * 1. if it has no namespace associated, it extracts the page's pathname, and the rest is given in the URL object
 * 2. if it has a namespace associated, it extracts the data associated with it, and sets in on the event object
 *
 * @private
 * @ignore
 * @param {URLChangeListener} listener The listener for which the event is being composed
 * @param {URL} previousUrl The page's previous URL
 * @param {URL} nextUrl The page's current URL
 * @param {URLChangeEventCache} urlDataCache The cache object
 * @return {*} {URLChangeEventCacheEntry} The cache entry with the event data
 */
function composeUrlChangeEvent(
  listener: URLChangeListener,
  previousUrl: URL,
  nextUrl: URL,
  urlDataCache: URLChangeEventCache,
) {
  const namespaceData = urlDataCache.get(listener.namespace || null);
  if (namespaceData != null) {
    return namespaceData;
  }

  if (listener.namespace == null) {
    const newNamespaceData: URLChangeEventCacheEntry = {
      hasChanged: previousUrl.href !== nextUrl.href,
      event: {
        from: {
          url: previousUrl,
          subPathname: getUrlSubPathname(previousUrl),
        },
        to: {
          url: nextUrl,
          subPathname: getUrlSubPathname(nextUrl),
        },
      },
    };

    urlDataCache.set(null, newNamespaceData);
    return newNamespaceData;
  }

  const fromData = getUrlNamespacedData(previousUrl, listener.namespace);
  const toData = getUrlNamespacedData(nextUrl, listener.namespace);
  const hasSubPathnameChanged = fromData.subPathname != toData.subPathname;
  const hasDataChanged = hasSubPathnameChanged || !util.areRecordsEqual(fromData.searchParams, toData.searchParams);

  const newNamespaceData: URLChangeEventCacheEntry = {
    hasChanged: hasDataChanged,
    event: {
      from: fromData,
      to: toData,
    },
  };

  urlDataCache.set(listener.namespace, newNamespaceData);
  return newNamespaceData;
}

/**
 * Internal function. Extracts all the data from `url` associated with the given `namespace`.
 *
 * @private
 * @ignore
 * @param {URL} url The URL from which to extract the data
 * @param {string} namespace The target namespace
 * @return {*}  {URLData} The resulting data
 */
function getUrlNamespacedData(url: URL, namespace: string): URLData {
  const router = getRouterObject();
  const subPathname = getUrlSubPathname(url, namespace);
  const searchParams = util.extractSearchParameters(url, namespace, router.mainNamespace);
  return {
    url,
    subPathname,
    searchParams,
  };
}

/**
 * Utility function. It extracts the pathname of the given `namespace` from the given `url`.
 * This includes looking for the pathname in the actual URL's pathname, and in the search parameters.
 *
 * If no `namespace` is provided. It will return the actual URL's pathname, which is the same as
 * requesting the main namespace pathname.
 *
 * The returned pathname is URI-decoded.
 *
 * @private
 * @ignore
 * @param {URL} url The URL from which to extract the pathname
 * @param {(string | null)} [namespace] The namespace for which to extract the pathname
 * @return {*} {string} The URL-decoded pathname for the namespace
 */
function getUrlSubPathname(url: URL, namespace?: string | null): string {
  const router = getRouterObject();
  if (namespace == null || namespace === router.mainNamespace) {
    // Assumption/Invariant: `router.basePathname` always ends with a slash
    // And subpathnames should also always start with a slash
    return url.pathname.substring(router.basePathname.length - 1) || '/';
  }
  const subPathnameKey = `${namespace}Path`;
  return url.searchParams.get(subPathnameKey) || '/';
}

/**
 *
 * Initialization function. Setups this library initial state and listeners.
 * It can be safely called multiple times.
 *
 * @export
 * @return {*}  {InternalState} this library internal state object.
 */
export function initRouting(): InternalState {
  if (routerObject) {
    return routerObject;
  }

  if (!isBrowser()) {
    routerObject = {
      mainNamespace: null,
      basePathname: '/',
      urlChangeListeners: [],
      currentUrl: createSSRUrl(),
      isInitialized: true,
    };
    return routerObject;
  }

  routerObject = getRouterObject();

  routerObject.currentUrl = new URL(String(window.location));
  routerObject.basePathname = util.extractBasePathname(routerObject.currentUrl, AEM_PAGE_DELIMITER);
  routerObject.isInitialized = true;

  window.addEventListener('popstate', globalPopStateListener);
  window.addEventListener('hashchange', globalPopStateListener);

  return routerObject;
}

/**
 * Sets the page's main namespace. Only the first call of this function have application effects.
 * Every subsequent call is a no-op with a console warning about this.
 *
 * On the first call of this method, the URL may be rewritten. In the cases where the main namespace
 * already has an associated pathname -- which, due to there not being any main component, would be stored
 * in search parameters, -- it's value will be removed from the search parameters and moved to the main URL's
 * pathname.
 *
 * Even though URL update effectively changes the URL's structure, it doesn't change the data as
 * interpreted by the routing library and its users. As such, no URL change event is fired for this change.
 *
 * @export
 * @param {string} namespace The main namespace
 * @return {*}  {void}
 * @throws {TypeError} if `namespace` is not a string literal
 */
export function setMainNamespace(namespace: string): void {
  if (typeof namespace != 'string') {
    throw new TypeError('[routing] namespace must be a string literal');
  }

  initRouting();
  const router = getRouterObject();
  if (router.mainNamespace != null) {
    // If the main namespace has already been set before, we always bail out and do nothing
    // But if someone tries to change it to a different namespace, log a warning message
    if (namespace != null && router.mainNamespace !== namespace) {
      // only log message if someone tried to register a different namespace
      LOGGER.warn('[sw-routing] refused re-set of main namespace. It can only be set once.');
    }
    return;
  }

  // This if above guarantees this only executes if there's not current main namespace
  // and the new namespace is valid (non-null)

  router.mainNamespace = namespace;

  const namespacePathKey = `${namespace}Path`;
  const pathname = router.currentUrl.searchParams.get(namespacePathKey);
  if (pathname != null && pathname.length > 0) {
    const fixedUrl = resolve({
      namespace,
      path: pathname,
      hash: router.currentUrl.hash,
    });
    if (fixedUrl != null) {
      // before URL normalisation we need to remove the searchParams Path
      // of the new mainNamespace
      fixedUrl.searchParams.delete(namespacePathKey);
      internalNavigateTo(fixedUrl, true, true);
    }
  } else {
    triggerUrlChange(true);
  }
  LOGGER.log(`[sw-routing] accepted main namespace ${router.mainNamespace}`);
}

/**
 * Returns the current main namespace, if any.
 * @returns {*} {InternalState} this library internal state object.
 */
export function getCurrentRouterObject(): InternalState {
  return routerObject;
}

/**
 * Returns or creates the internal state for this library.
 *
 * The first time this function is called, it will create the library's internal state
 * and hook it up to the top window. Futher calls to this method will simply return that
 * same object.
 *
 * **Note:** the create state object is *uninitialised*, to correctly initialise the
 * internal state see the `initRouting` function.
 *
 * @private
 * @ignore
 * @return {*}  {InternalState} this library internal state object.
 */
function getRouterObject(): InternalState {
  if (routerObject) {
    return routerObject;
  }

  if (isBrowser()) {
    const currentUrl = new URL(String(window.location));

    routerObject = {
      mainNamespace: null,
      basePathname: util.extractBasePathname(currentUrl, AEM_PAGE_DELIMITER),
      urlChangeListeners: [],
      currentUrl: currentUrl,
      isInitialized: false,
    };

    return routerObject;
  }

  routerObject = {
    mainNamespace: null,
    basePathname: '/',
    urlChangeListeners: [],
    currentUrl: createSSRUrl(),
    isInitialized: false,
  };

  return routerObject;
}

/**
 * Navigates to the target `url`. This parameter MUST BE one obtained via the `resolve` function of
 * this library. Navigation to other URLs are still executed, but the behaviour of the library becomes
 * undefined and MUST NOT BE relied upon, as it is not guaranteed between releases.
 *
 * If the given `url` is equal to the current URL, this function is a no-op.
 *
 * @export
 * @param {URL} url - The URL to navigate to
 * @param {boolean} [replace=false] A flag indicating if the new URL should be replace the History's
 * top entry (`true`), or be pushed into it (`false`)
 */
export function navigateTo(url: URL, replace = false): void {
  initRouting();
  internalNavigateTo(url, replace, true);
}

/**
 * Internal function for the exported `navigateTo`. Supports one additional parameter for internal use
 * only: `shouldTriggerListeners` which defaults to `true`; if `false` the URL change will not trigger
 * any registered `URLChangeListener`.
 *
 * Even if `shouldTriggerListeners` is `true`, listeners will not be called if the target `url` is the
 * same as the current page's URL.
 *
 * @private
 * @ignore
 * @param {URL} url - The URL to navigate to
 * @param {boolean} [replace=false] A flag indicating if the new URL should be replace the History's
 * top entry (`true`), or be pushed into it (`false`)
 * @param {boolean} [shouldTriggerListeners=true] A flag indicating if the registered `URLChangeListener`s
 * should be called for this navigation or not.
 */
function internalNavigateTo(url: URL, replace = false, shouldTriggerListeners = true): void {
  initRouting();
  const router = getRouterObject();
  const isNavigatingToCurrentUrl = url.href === router.currentUrl.href;

  navigateToUrl(url, replace, shouldTriggerListeners && !isNavigatingToCurrentUrl, !hasSharedBase(url));
}

/**
 * Adds a listener to be called every time a URL change is detected.
 *
 * The optional `options` parameter supports the following property:
 * - `namespace` - if provided, the listener will only be trigger if the data associated with the
 * given namespace has changed from one URL to the next, and it's data will be parsed and given in
 * fired event
 *
 * For more information on the event fired to listeners, see the `URLChangeEvent` type.
 *
 * @export
 * @param {URLChangeListenerFunction} listener - The listener to add
 * @param {URLChangeOptions} [options]
 */
export function addUrlChangeListener(listener: URLChangeListenerFunction, options?: URLChangeOptions): void {
  const router = getRouterObject();
  router.urlChangeListeners.push({
    ...options,
    handler: listener,
  });
}

/**
 * Removes a previously added listener.
 *
 * @export
 * @param {URLChangeListenerFunction} listener - The listener to remove
 */
export function removeUrlChangeListener(listener: URLChangeListenerFunction): void {
  const router = getRouterObject();
  const index = router.urlChangeListeners.findIndex((storedListener) => storedListener.handler === listener);
  if (index >= 0) {
    router.urlChangeListeners.splice(index, 1);
  }
}

// TODO: add support for URL clearing (remove searchParams and stuff)
/**
 * Constructs a `URL` object by merging the properties of the provided `options` object with
 * the current page's URL. The return of this function is what should be passed to calls of
 * `navigateTo`.
 *
 * The behaviour of each property is as follows:
 * * `path` - If `namespace` matches the main routing namespace, then the current non-AEM path
 * is replaced with this one. If `namespace` does NOT match the main routing namespace, a `searchParam`
 * is added with key `${namespace}Path` and the value of the path.
 * * `hash` - Replaces the hash of the current URL, all else remains unchanged
 * * `searchParams` - Adds the searchParams to the current URL, everything is kept except for
 * searchParams with the same keys, which are overriden. Blacklisted params are: ['Path']
 *
 * This method also accepts an array of the given options and constructs them into an URL.
 *
 * Furthermore, this method can also resolve a clear link if the correct clear options are passed in the urlGlobalOptions
 * object. The clear resolve can happen in two ways:
 * 1- resolved is called with clear: true (within the urlGlobalOptions) -> it resolves a clean link to the same page.
 * 2- resolved is called with base: 'OtherPage.html' (within the urlGlobalOptions) -> it resolves a clean link to a new page
 * passed in the base option.
 *
 * All the parameters will work as before but with clean links.
 *
 * @export
 * @param {URLInitOptions | URLInitOptions[] | null} urlNamespaceOptions The options for generating the URL
 * @param {URLGlobalOptions} urlGlobalOptions The options for clearing URL parameters
 * @return {*}  {(URL | null)} The current page's URL, but with the options applied to it.
 */
export function resolve(
  urlNamespaceOptions: URLInitOptions | URLInitOptions[] | null,
  urlGlobalOptions?: URLGlobalOptions,
): URL | null {
  initRouting();

  if (!validateURLInitOptions(urlNamespaceOptions, urlGlobalOptions)) {
    return null;
  }

  const { target, isSamePage } = getResolveRebasedTarget(urlGlobalOptions);

  if (Array.isArray(urlNamespaceOptions)) {
    urlNamespaceOptions.forEach((namespaceOption: URLInitOptions) => {
      resolveNamespaceOptions(target, namespaceOption, isSamePage);
    });
  } else if (urlNamespaceOptions) {
    resolveNamespaceOptions(target, urlNamespaceOptions, isSamePage);
  }
  return target;
}

/**
 *  modifies a `URL` object by adding the searchParams of the provided `options` object
 * @param { URLModifierOptions | URLModifierOptions[] } urlOptions The options to modify the URL
 * @returns {*}  {(URL | URL[] | null)} The modified URL(URLs) with the options applied to it.
 */
export function resolveSearchParams(urlOptions: URLModifierOptions | URLModifierOptions[]): URL | URL[] | null {
  initRouting();

  if (!validateURLModifierOptions(urlOptions)) {
    return null;
  }

  if (Array.isArray(urlOptions)) {
    return urlOptions.map((option: URLModifierOptions) => {
      return resolveSearchParamsOptions(option);
    });
  } else {
    return resolveSearchParamsOptions(urlOptions);
  }
}

/**
 * Validates that one or more `URLInitOptions` object is well defined. Namely:
 * 1. Is non-null
 * 2. Is an object or an array of objects
 * 3. Each object has a namespace parameter
 *
 * **Note:** This function has side-effects, namely, console warnings.
 *
 * @private
 * @ignore
 * @param {(URLInitOptions | URLInitOptions[] | null)} options The options to validate
 * @param {URLGlobalOptions} urlGlobalOptions The options for clearing URL parameters
 * @return {*} {boolean} `true` if it is valid, `false` otherwise.
 */
function validateURLInitOptions(
  urlNamespaceOptions: URLInitOptions | URLInitOptions[] | null,
  urlGlobalOptions?: URLGlobalOptions,
) {
  if (urlNamespaceOptions == null || typeof urlNamespaceOptions != 'object') {
    if (urlGlobalOptions == null || typeof urlNamespaceOptions != 'object') {
      LOGGER.warn(
        '[sw-routing] resolve arguments should contain an URLInitOption object or array or an URLGlobalOptions object',
      );
      return false;
    }
  } else if (Array.isArray(urlNamespaceOptions)) {
    if (urlNamespaceOptions.length === 0) {
      LOGGER.warn('[sw-routing] Namespace options argument must contain at least one URLInitOption object');
      return false;
    }

    const areURLInitOptionsValid = urlNamespaceOptions.every((option: URLInitOptions) => {
      return isValidURLInitOptions(option);
    });

    if (!areURLInitOptionsValid) {
      LOGGER.warn('[sw-routing] Namespace options must be an object with a "namespace" string property.');
      return false;
    }
  } else {
    const isOptionsValid = isValidURLInitOptions(urlNamespaceOptions);
    if (!isOptionsValid) {
      LOGGER.warn('[sw-routing] Namespace options must contain a "namespace" string property.');
      return false;
    }
  }
  return true;
}

/**
 * Validates that one or more `URLInitOptions` object is well defined. Namely:
 * 1. Is non-null
 * 2. Is an object or an array of objects
 * 3. Each object has a searchParams property
 *
 * **Note:** This function has side-effects, namely, console warnings.
 *
 * @private
 * @ignore
 * @param urlOptions options The options to validate
 * @returns {*} {boolean} `true` if it is valid, `false` otherwise.
 */
function validateURLModifierOptions(urlOptions: URLModifierOptions | URLModifierOptions[]) {
  if (urlOptions == null || typeof urlOptions != 'object') {
    LOGGER.warn('[sw-routing] resolveSearchParams arguments should contain an URLModifierOptions object or array.');
    return false;
  } else if (Array.isArray(urlOptions)) {
    if (urlOptions.length === 0) {
      LOGGER.warn('[sw-routing] resolveSearchParams argument must contain at least one URLModifierOptions object.');
      return false;
    }

    const result = urlOptions.filter((option: URLModifierOptions) => option.searchParams == null || option.url == null);
    if (result.length > 0) {
      LOGGER.warn('[sw-routing] all resolveSearchParams objects must contain a searchParams object.');
      return false;
    }
  } else {
    if (urlOptions.searchParams == null || urlOptions.url == null) {
      LOGGER.warn('[sw-routing] resolveSearchParams must contain an url and a searchParams object.');
      return false;
    }
  }

  return true;
}

/**
 * Derives the base URL in which `resolve`'s `URLInitOptions` operations should be performed.
 * This base URL is derived from the given `urlGlobalOptions`.
 *
 *
 * In the normal case where no options are provided, the base URL is the router's current URL.
 * But if options are provided, this may change:
 * * when `clear` is `true`: the base URL will be the current URL, but any state will be cleared
 * * when `base` is some string: the base URL will be different (it will point to some page outside of this router instance's domain)
 *
 *
 * @private
 * @ignore
 * @param {URLInitOptions} options The options object
 * @return {*} {ResolutionTarget} the rebased URL target for URL resolution
 */
function getResolveRebasedTarget(urlGlobalOptions?: URLGlobalOptions): ResolutionTarget {
  const router = getRouterObject();
  const target = new URL(String(router.currentUrl));

  if (!urlGlobalOptions) {
    return { target, isSamePage: true };
  }

  const hasBasePathnameModification = typeof urlGlobalOptions.base === 'string' && urlGlobalOptions.base.length > 0;

  if (hasBasePathnameModification && urlGlobalOptions.base) {
    // since rebasing implies a clear, we don't need to worry about keeping
    // the main routing component pathname here, so we modify the URL's pathname
    // without caring about it's state
    target.pathname = urlGlobalOptions.base;
    target.search = '';
    target.hash = '';
    return { target, isSamePage: false };
  }

  if (urlGlobalOptions.clear) {
    target.pathname = router.basePathname;
    target.search = '';
    target.hash = '';
    return { target, isSamePage: true };
  }

  return { target, isSamePage: true };
}

/**
 * Validates if the given `options` has the required properties
 *
 * @private
 * @ignore
 * @param {URLInitOptions} options The options object
 * @return {*} {boolean} returns true if is valid, false otherwise
 */
function isValidURLInitOptions(options: URLInitOptions): boolean {
  if (typeof options.namespace !== 'string') {
    return false;
  }
  return true;
}

/**
 * Modifies the given `target` URL according to the `options` object
 *
 * @private
 * @ignore
 * @param {URL} targetUrl The target URL
 * @param {URLInitOptions} options The URLInitOptions object
 * @return {*} {void}
 * resolveNamespaceOptions
 */
function resolveNamespaceOptions(target: URL, options: URLInitOptions, isSamePage: boolean) {
  const router = getRouterObject();
  const searchParams = options.searchParams;

  const targetPathname = options.pathname || options.path;

  if (targetPathname) {
    const subPathname = targetPathname.startsWith('/') ? targetPathname : `/${targetPathname}`;
    if (options.namespace === router.mainNamespace && isSamePage) {
      // `subPathname` is the URL path after the AEM page path, so we append rather than replace
      let basePath = router.basePathname;
      if (router.basePathname.endsWith('/')) {
        // preventing double bar for non AEM environments by checking if the basePathname is '/'
        basePath = router.basePathname.substring(0, router.basePathname.length - 1);
      }
      target.pathname = `${basePath}${subPathname}`;
    } else {
      target.searchParams.set(`${options.namespace}Path`, subPathname);
    }
  }

  if (options.hash) {
    target.hash = options.hash;
  }

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (BLACKLISTED_SEARCH_PARAMETERS.includes(key)) {
        // Checks if key is part of blacklisted parameters
        LOGGER.warn(`[sw-routing] Using '${key}' as a search param is not allowed and will be ignored.`);
        return;
      }
      const namespacedKey = `${options.namespace}${key}`;
      if (value == null) {
        target.searchParams.delete(namespacedKey);
      } else {
        target.searchParams.set(namespacedKey, value);
      }
    });
  }
}

/**
 * Modifies a given URL appending the searchParams according to the `options` object
 *
 * @private
 * @ignore
 * @param {URLModifierOptions} options The URLModifierOptions object
 * @return {*} {URL} the modified URL of the options object
 * resolveSearchParamsOptions
 */
function resolveSearchParamsOptions(options: URLModifierOptions): URL {
  const { searchParams } = options;
  const url = new URL(options.url?.toString());

  Object.entries(searchParams).forEach(([key, value]) => {
    if (BLACKLISTED_SEARCH_PARAMETERS.includes(key)) {
      // Checks if key is part of blacklisted parameters
      LOGGER.warn(`[sw-routing] Using '${key}' as a search param is not allowed and will be ignored.`);
      return;
    }

    if (value == null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value.toString());
    }
  });

  return url;
}

/**
 * Tries to match the given `namespace`/`matchString` to the current URL.
 *
 * The match string has the following format:
 * 1. A url pathname, starting with a slash: `/foo/bar`
 * 2. A search parameter string, starting with a question mark: `?foo=bar&doo`
 * 3. A hash string, starting with the hash symbol: `#someHash`
 *
 * All of those parameters can be present in the match string, but they must come in that order.
 * For example, the string `/foo?bar=baz` would only return true it the given namespace had the
 * `/foo` path and the `bar=baz` search parameter.
 *
 * @export
 * @param {string} namespace The target namespace for the match
 * @param {string} matchString The match string
 * @param {URLMatchOptions} matchOptions The options for matching URL pathnames
 * @return {*}  {URLMatchResult} The resulting match object
 */
export function match(namespace: string, matchString: string, matchOptions?: URLMatchOptions): URLMatchResult;

/**
 * Tries to match the given URLOptions to the current URL
 *
 * @export
 * @param {URLInitOptions} query
 * @param {URLMatchOptions} matchOptions
 * @returns {*} {URLMatchResult} The resulting match object
 */
export function match(query: URLInitOptions, matchOptions?: URLMatchOptions): URLMatchResult;

/**
 * Tries to match the given array of URLOptions to the current URL.
 *
 * @export
 * @param {URLInitOptions[]} queries
 * @param {URLMatchOptions} matchOptions
 * @returns {*} {URLMultipleMatchResult} The resulting match object
 */
export function match(queries: URLInitOptions[], matchOptions?: URLMatchOptions): URLMultipleMatchResult;

// TODO: This logic is currently somewhat weird, and should be reviewed.
// TODO: What kind of query data extraction we want (is `/foo/:id/` enough, or
// do we want something more, like vue3's `/foo/:id(\d+)`?)
/**
 * Tries to match the given Option(s)/matchString to the current URL.
 *
 * The pathname may contain wildcard parameters, such as `/foo/:bar`. In these cases
 * the actual value of the second path part will be extracted from the URL and returned in the
 * `variables` property of the returned result, as `{ bar: 'barValue' }`.
 *
 * Furthermore, if you want to match the exact pathname you need to to provide a `matchOptions` object with
 * the boolean `exact`. Example:
 *    (assume pathname is: /model/class-a)
 *    Default behaviour -> match('', '/model').isMatch  // true
 *    Exact option behaviour -> match('', '/model').isMatch  // false
 *    Exact option behaviour -> match('', '/model/class').isMatch  // false
 *    Exact option behaviour -> match('', '/model/class-a').isMatch  // true
 *
 * The result of this call will return an URLMatchResult object (or URLMultipleMatchResult if you passed
 * multiple options), containing the following information:
 *    1. isMatch - if your query matches the current URL
 *    2. variables - if wildcard parameters are passed, they will be provided in this object
 *    3. searchParams - searchParameters passed in the query will be added to this object
 *    4. namespaces - ONLY if you pass an array of URLInitOptions, this object contains the variables and searchParameters objects
 *
 * @param {string | URLInitOptions | URLInitOptions[]} queriesOrNamespace
 * @param {string | URLMatchOptions} matchStringOrOptions
 * @param {URLMatchOptions} matchOptions
 * @returns {*} {URLMatchResult | URLMultipleMatchResult} The resulting match object
 */
export function match(
  queriesOrNamespace: string | URLInitOptions | URLInitOptions[],
  matchStringOrOptions?: string | URLMatchOptions,
  matchOptions?: URLMatchOptions,
): URLMatchResult | URLMultipleMatchResult {
  initRouting();

  if (typeof queriesOrNamespace === 'string' && typeof matchStringOrOptions === 'string') {
    const option = util.convertMatchStringToURLOptions(queriesOrNamespace, matchStringOrOptions);
    return internalMatch(option, matchOptions);
  } else if (typeof queriesOrNamespace === 'object' && !Array.isArray(queriesOrNamespace)) {
    return internalMatch(queriesOrNamespace, matchOptions);
  } else if (typeof queriesOrNamespace === 'object' && Array.isArray(queriesOrNamespace)) {
    const matchResults: URLMultipleMatchResult = {
      isMatch: false,
      namespaces: {},
    };
    if (queriesOrNamespace.length > 0) {
      for (const option of queriesOrNamespace) {
        const result = internalMatch(option, matchOptions);
        if (!result.isMatch) {
          return matchResults;
        } else if (matchResults.namespaces) {
          matchResults.namespaces[option.namespace] = {
            variables: result.variables,
            searchParams: result.searchParams,
          };
        }
      }
      matchResults.isMatch = true;
      return matchResults;
    }
  }
  return {
    isMatch: false,
    variables: void 0,
    searchParams: void 0,
  };
}

function internalMatch(option: URLInitOptions, matchOptions?: URLMatchOptions): URLMatchResult {
  const result: URLMatchResult = {
    isMatch: false,
    variables: void 0,
    searchParams: void 0,
  };
  const { namespace, searchParams, hash } = option;
  const pathname = option.pathname || option.path;

  const router = getRouterObject();

  if (!isValidURLInitOptions(option)) {
    LOGGER.warn('[sw-routing] match option is invalid.');
    return result;
  }

  if (pathname) {
    if (pathname === '*') {
      result.isMatch = true;
      return result;
    }

    const pathnameQuery = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const namespacePathname = getUrlSubPathname(router.currentUrl, namespace);

    if (doesQueryRequireExtraction(pathnameQuery)) {
      const match = extractPathnameParameters(pathnameQuery, namespacePathname);

      if (match != null) {
        result.isMatch = true;
        result.variables = match;
        // notice the no return. match has started, but it may still need hash and search matches
      } else {
        return result;
      }
    } else {
      result.isMatch = matchOptions?.exact
        ? namespacePathname === pathnameQuery
        : namespacePathname.startsWith(pathnameQuery);
      if (!result.isMatch) {
        return result;
      }
      // notice the no return on match success
      // match has started, but it may still need hash and search matches
    }
  }

  if (searchParams && Object.entries(searchParams).length > 0) {
    const isFullMatch = Object.entries(searchParams).every(([key, value]) => {
      const namespacedKey = `${namespace}${key}`;
      if (!router.currentUrl.searchParams.has(namespacedKey)) {
        return false;
      }

      // No requirement for value, mere presence is a match
      if (value == null || value === '') {
        return true;
      }

      return value === router.currentUrl.searchParams.get(namespacedKey);
    });
    result.isMatch = isFullMatch;
    if (!isFullMatch) {
      return result;
    }
    // notice the no return on match success
    // match has started, but it may still need hash
  }

  if (hash) {
    result.isMatch = hash === router.currentUrl.hash.substring(1);
  }

  result.searchParams = getUrlNamespacedData(router.currentUrl, namespace).searchParams;

  return result;
}

/**
 * Given a `pathname` and a query over it `pathnameQuery`, this function will extract the
 * name query parameters from the pathname into a plain javascript object. If the query does
 * not match the pathname, `null` is returned instead.
 *
 * For example, given the parameters:
 * - `pathname`: `/foo/bar`
 * - `pathnameQuery` `/foo/:name`
 * The output will be `{ name: 'bar' }`
 *
 * @private
 * @ignore
 * @param {string} pathnameQuery The pathname query string
 * @param {string} pathname The pathname to query
 * @return {*}  {(Record<string, string> | null)} The resulting key-value object extracted,
 * or `null` if the query does not match the pathname
 */
function extractPathnameParameters(pathnameQuery: string, pathname: string): Record<string, string> | null {
  const pathnameRegexp = pathToRegexp(pathnameQuery);
  const pathnameValueMatch = pathnameRegexp.regexp.exec(pathname);
  const pathnameKeyMatch = pathnameRegexp.regexp.exec(pathnameQuery);
  if (pathnameValueMatch == null || pathnameKeyMatch == null) {
    // The pathname query does not match the current pathname URL, so this is an invalid match
    return null;
  }

  const [, ...values] = pathnameValueMatch;
  const [, ...keys] = pathnameKeyMatch;
  return keys.reduce((result: Record<string, string>, key: string, index: number) => {
    result[key.substr(1)] = values[index];
    return result;
  }, {});
}

/**
 * Checks if the given `pathnameQuery` needs pathname value extraction.
 *
 * @private
 * @ignore
 * @param {string} pathnameQuery the pathname query string to check
 * @return {*}  {boolean} `true` if it requires value extraction, `false` otherwise
 */
function doesQueryRequireExtraction(pathnameQuery: string): boolean {
  return pathnameQuery.indexOf(':') >= 0;
}

/**
 * Internal concretization of the exported `navigateTo` function. It changes the page's URL
 * to the given `url`, replacing or pushing onto the stack depending on the value of `replace`,
 * and triggering all the registered URLChangeEvents if `shouldTriggerListeners` is `true`.
 *
 * @private
 * @ignore
 * @param {URL} url the tartget URL
 * @param {boolean} replace `true` if the current History API entry should be replaced, `false` if it
 * should be pushed instead
 * @param {boolean} shouldTriggerListeners `true` to trigger all registered listeners, `false` to not
 * do that
 */
function navigateToUrl(
  url: URL,
  replace: boolean,
  shouldTriggerListeners: boolean,
  isDifferentPageNavigation?: boolean,
) {
  if (!isBrowser()) {
    return;
  }

  if (isDifferentPageNavigation) {
    const target = window.top || window;
    target.location.href = url.href;
    return;
  }
  if (replace) {
    window.history.replaceState({ from: 'url' }, '', url.href);
  } else {
    window.history.pushState({ from: 'url' }, '', url.href);
  }
  if (shouldTriggerListeners) {
    triggerUrlChange();
  }
}

/**
 * Compares a given base to the current page router base. If an empty string is passed the method
 * will return true (assumes a shared base).
 *
 * The method can either receive a simple string as the base to be compared or an
 * URL that will be used to extract the base that will compared with the current one.
 *
 * @export
 * @param {string | URL} base A base string or URL to compare with the current base
 * @return {*}  {boolean} boolean that indicates if the base is shared
 */

export function hasSharedBase(base: string | URL): boolean {
  initRouting();
  const router = getRouterObject();

  if (typeof base === 'string') {
    if (base.length > 0) {
      const baseToCompare = base.endsWith('/') ? base : base + '/';
      return router.basePathname === baseToCompare;
    }
    return true;
  }
  if (base instanceof URL) {
    // we assume that the base argument is an url
    const urlBase = util.extractBasePathname(base, AEM_PAGE_DELIMITER);
    return router.basePathname === urlBase;
  }
  return false;
}

/**
 * Extracts all the data from the current `url` associated with the given `namespace`.
 *
 * @param {string} namespace The target namespace
 * @return {*}  {URLData} The resulting data
 */
export function getNamespaceData(namespace: string): URLData {
  initRouting();
  const router = getRouterObject();
  return getUrlNamespacedData(router.currentUrl, namespace);
}
